import React, { useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { isPro, hasProEntitlement } from '../../utils/entitlements';
import { openBillingPortal } from '../../services/billing';
import { BILLING_ENABLED, PRICING } from '../../config/plans';
import { UpgradeModal } from './UpgradeModal';

/**
 * Billing section for the settings panel. Shows the current plan and a way to
 * manage (Stripe Customer Portal) or upgrade. Hidden entirely while billing is
 * dormant so it doesn't confuse users before launch.
 */
export const BillingSettings: React.FC = () => {
  const { effectiveProfile } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!BILLING_ENABLED) return null;

  const pro = isPro(effectiveProfile);
  // Distinguish a paying subscriber from a comped admin/dev (who is "pro" via role).
  const subscriber = hasProEntitlement(effectiveProfile);
  const periodEnd = effectiveProfile?.currentPeriodEnd?.toDate?.();
  const cancelAtPeriodEnd = effectiveProfile?.cancelAtPeriodEnd;
  const interval = effectiveProfile?.billingInterval;

  const handleManage = async () => {
    setBusy(true);
    setError(null);
    try {
      await openBillingPortal(); // redirects away
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not open billing portal.');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-white">Plan</div>
          <div className="text-xs text-gray-400">
            {pro ? (subscriber ? 'Pro' : 'Pro (complimentary)') : 'Free'}
          </div>
        </div>
        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
            pro ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-700 text-gray-300'
          }`}
        >
          {pro ? 'PRO' : 'FREE'}
        </span>
      </div>

      {subscriber && periodEnd && (
        <div className="text-xs text-gray-400">
          {cancelAtPeriodEnd
            ? `Access ends ${periodEnd.toLocaleDateString()}`
            : `Renews ${periodEnd.toLocaleDateString()}`}
          {interval ? ` · billed ${interval === 'year' ? 'annually' : 'monthly'}` : ''}
        </div>
      )}

      {subscriber ? (
        <button
          onClick={handleManage}
          disabled={busy}
          className="w-full px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {busy ? 'Opening…' : 'Manage subscription'}
        </button>
      ) : !pro ? (
        <button
          onClick={() => setShowUpgrade(true)}
          className="w-full px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition-colors"
        >
          Upgrade to Pro — from {PRICING.year.label}/yr
        </button>
      ) : null}

      {error && <div className="text-xs text-red-400">{error}</div>}

      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} />
    </div>
  );
};
